import React, { useState, useMemo } from "react";
import { SensorKit } from "@/sensorKits/sensorKit";
import { SensorKitFilters, useSensorKitsInfinite } from "@/sensorKits/api";
import { SensorKitAlertFilters, sensorKitAlerts, alertLogs } from "@/alerts/api";
import { SensorKitAlert } from "@/alerts/alert";
import { Card } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { ChevronDownIcon, ChevronRightIcon } from "lucide-react";
import { ThaloDataTable } from "@/components/thalo-data-table";
import { ColumnDef } from "@tanstack/react-table";
import { SensorKitDialog } from "./SensorKitDialog";
import { format, subHours, addHours } from "date-fns";
import { useQueries } from "@tanstack/react-query";
import { SortingState } from "@tanstack/react-table";
import { ExternalLink } from "lucide-react";

// Extended type for sensor kit with alert data
type SensorKitWithAlert = SensorKit & {
  alert?: SensorKitAlert;
  firstFiringTime?: string | null; // First time the alert fired
};

// Helper function to check if alert_type indicates firing
const isFiringAlert = (alertType: string): boolean => {
  return alertType.includes('_to_firing') || alertType === 'firing';
};

// Note: Changed from const sensorKitColumns to a function createSensorKitColumns
// because we need to conditionally add alert columns only when alertFilterType is "offline" or "missing_data"
const createSensorKitColumns = (hasAlertData: boolean): ColumnDef<SensorKitWithAlert>[] => {
  const baseColumns: ColumnDef<SensorKitWithAlert>[] = [
    {
      accessorKey: "serial_number",
      header: "Serial Number",
    },
    {
      accessorKey: "config.model",
      header: "Model",
      cell: ({ row }) => {
        const model = row.original.config.model;
        return model.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      },
    },
    {
      accessorKey: "config.version",
      header: "Version",
    },
    {
      accessorKey: "current_installation",
      header: "Installation",
      cell: ({ row }) => {
        return row.original.current_installation ? row.original.current_installation.name : '';
      },
    },
  ];

  if (hasAlertData) {
    baseColumns.push(
      {
        accessorKey: "firstFiringTime",
        header: "First Message Received",
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          // Use first firing time if available, otherwise fall back to first_message_received
          const aTime = rowA.original.firstFiringTime || rowA.original.alert?.first_message_received;
          const bTime = rowB.original.firstFiringTime || rowB.original.alert?.first_message_received;
          
          // Items without alert data go to the bottom
          if (!aTime && !bTime) return 0;
          if (!aTime) return 1;
          if (!bTime) return -1;
          
          return new Date(aTime).getTime() - new Date(bTime).getTime();
        },
        cell: ({ row }) => {
          // Use first firing time if available, otherwise fall back to first_message_received
          const timeToDisplay = row.original.firstFiringTime || row.original.alert?.first_message_received;
          if (!timeToDisplay) return '-';
          try {
            return format(new Date(timeToDisplay), "MMM d, yyyy HH:mm");
          } catch {
            return timeToDisplay;
          }
        },
      },
      {
        accessorKey: "grafanaLink",
        header: "Grafana",
        cell: ({ row }) => {
          const sensorKit = row.original;
          const firstFiringTime = sensorKit.firstFiringTime || sensorKit.alert?.first_message_received;
          
          if (!firstFiringTime || !sensorKit.serial_number) return '-';
          
          try {
            const firstFiringDate = new Date(firstFiringTime);
            const fromTime = subHours(firstFiringDate, 12);
            const toTime = addHours(firstFiringDate, 12);
            
            // Format dates as ISO strings for Grafana
            const fromISO = fromTime.toISOString();
            const toISO = toTime.toISOString();
            
            // Build Grafana URL
            const grafanaUrl = `https://thalolabs.grafana.net/d/bdwwpv7kfudq8b/thalorefrigerant-internal?orgId=1&from=${encodeURIComponent(fromISO)}&to=${encodeURIComponent(toISO)}&timezone=browser&var-Nodes=${encodeURIComponent(sensorKit.serial_number)}`;
            
            return (
              <a
                href={grafanaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
              >
                <span>View in Grafana</span>
                <ExternalLink className="h-3 w-3" />
              </a>
            );
          } catch {
            return '-';
          }
        },
      }
    );
  }

  return baseColumns;
};

interface SensorKitTablePanelProps {
  title: string;
  titleColor: string;
  filters: SensorKitFilters;
  additionalFilters: Partial<SensorKitFilters>;
  alertFilterType?: "offline" | "missing_data" | "out_of_spec";
}

export const SensorKitTablePanel = ({ title, titleColor, filters, additionalFilters, alertFilterType }: SensorKitTablePanelProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedSensorKit, setSelectedSensorKit] = useState<SensorKit | null>(null);
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState(false);

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useSensorKitsInfinite({
    ...filters,
    ...additionalFilters,
  });

  // Flatten all pages into a single array
  const sensorKits = useMemo(() => {
    return data?.pages.flatMap(page => page.results) || [];
  }, [data]);

  // Get total count from first page (will be updated after filtering if alertFilterType is set)
  const totalCountFromAPI = data?.pages[0]?.count || 0;

  // Prepare alert filter queries for useQueries
  const alertQueryConfigs = useMemo(() => {
    if (!alertFilterType || sensorKits.length === 0) return [];
    
    return sensorKits.map(sensorKit => {
      const alertFilters: SensorKitAlertFilters = {};
      switch (alertFilterType) {
        case "offline":
          alertFilters.sensor_kit_offline = sensorKit.serial_number;
          break;
        case "missing_data":
          alertFilters.sensor_kit_missing_data = sensorKit.serial_number;
          break;
        case "out_of_spec":
          alertFilters.sensor_kit_out_of_spec = sensorKit.serial_number;
          break;
      }
      return { 
        sensorKit, 
        queryKey: sensorKitAlerts.list(alertFilters).queryKey,
        queryFn: sensorKitAlerts.list(alertFilters).queryFn
      };
    });
  }, [alertFilterType, sensorKits]);

  // Fetch alerts for all sensor kits in parallel
  const alertQueries = useQueries({
    queries: alertQueryConfigs.map(config => ({
      queryKey: config.queryKey,
      queryFn: config.queryFn,
      enabled: alertFilterType === "offline" || alertFilterType === "missing_data"
    }))
  });

  // Create a map of serial_number -> alert (first alert from results)
  const alertMap = useMemo(() => {
    const map = new Map<string, SensorKitAlert>();
    alertQueries.forEach((query, index) => {
      if (query.data?.results && query.data.results.length > 0) {
        const sensorKit = alertQueryConfigs[index].sensorKit;
        map.set(sensorKit.serial_number, query.data.results[0]);
      }
    });
    return map;
  }, [alertQueries, alertQueryConfigs]);

  // Prepare alert log queries to find first firing time for each alert
  const alertLogQueryConfigs = useMemo(() => {
    if (!alertFilterType || alertMap.size === 0) return [];
    
    return Array.from(alertMap.values()).map(alert => ({
      alert,
      queryKey: alertLogs.list({ alert_id: alert.id }).queryKey,
      queryFn: alertLogs.list({ alert_id: alert.id }).queryFn
    }));
  }, [alertMap, alertFilterType]);

  // Fetch alert logs for all alerts in parallel
  const alertLogQueries = useQueries({
    queries: alertLogQueryConfigs.map(config => ({
      queryKey: config.queryKey,
      queryFn: config.queryFn,
      enabled: alertFilterType === "offline" || alertFilterType === "missing_data"
    }))
  });

  // Create a map of alert_id -> first firing time
  const firstFiringTimeMap = useMemo(() => {
    const map = new Map<number, string>();
    alertLogQueries.forEach((query, index) => {
      if (query.data?.results && query.data.results.length > 0) {
        const alert = alertLogQueryConfigs[index].alert;
        const allLogs = query.data.results;
        
        // Sort chronologically (oldest first) to find the first firing
        const chronologicalLogs = [...allLogs].sort((a, b) => 
          new Date(a.time).getTime() - new Date(b.time).getTime()
        );
        
        // Find the first log entry where the alert fired
        const firstFiringLog = chronologicalLogs.find(log => isFiringAlert(log.alert_type));
        
        if (firstFiringLog) {
          map.set(alert.id, firstFiringLog.time);
        }
      }
    });
    return map;
  }, [alertLogQueries, alertLogQueryConfigs]);

  // Enrich sensor kits with alert data and first firing time, filter out those without alerts, and sort by first firing time (most recent first)
  const sensorKitsWithAlerts = useMemo(() => {
    const enriched = sensorKits.map(sensorKit => {
      const alert = alertMap.get(sensorKit.serial_number);
      const firstFiringTime = alert ? firstFiringTimeMap.get(alert.id) : null;
      return {
        ...sensorKit,
        alert,
        firstFiringTime
      };
    }) as SensorKitWithAlert[];
    
    // When alertFilterType is set, only show sensor kits that actually have alerts
    const filtered = (alertFilterType === "offline" || alertFilterType === "missing_data" || alertFilterType === "out_of_spec")
      ? enriched.filter(sensorKit => sensorKit.alert !== undefined)
      : enriched;
    
    // Sort by first firing time (or first_message_received as fallback) descending (most recent first) when alert columns are shown
    if (alertFilterType === "offline" || alertFilterType === "missing_data") {
      return filtered.sort((a, b) => {
        // Use first firing time if available, otherwise fall back to first_message_received
        const aTime = a.firstFiringTime || a.alert?.first_message_received;
        const bTime = b.firstFiringTime || b.alert?.first_message_received;
        
        // Items without alert data go to the bottom (shouldn't happen after filtering, but just in case)
        if (!aTime && !bTime) return 0;
        if (!aTime) return 1;
        if (!bTime) return -1;
        
        // Sort descending (most recent first)
        return new Date(bTime).getTime() - new Date(aTime).getTime();
      });
    }
    
    return filtered;
  }, [sensorKits, alertMap, firstFiringTimeMap, alertFilterType]);

  // Use filtered count when alertFilterType is set, otherwise use API count
  const totalCount = useMemo(() => {
    if (alertFilterType && (alertFilterType === "offline" || alertFilterType === "missing_data" || alertFilterType === "out_of_spec")) {
      return sensorKitsWithAlerts.length;
    }
    return totalCountFromAPI;
  }, [alertFilterType, sensorKitsWithAlerts.length, totalCountFromAPI]);

  // Create columns based on whether we have alert data
  const columns = useMemo(() => {
    return createSensorKitColumns(alertFilterType === "offline" || alertFilterType === "missing_data");
  }, [alertFilterType]);

  // Default sorting by first firing time descending (most recent first) when alert columns are shown
  const defaultSorting: SortingState = useMemo(() => {
    if (alertFilterType === "offline" || alertFilterType === "missing_data") {
      return [{ id: "firstFiringTime", desc: true }];
    }
    return [];
  }, [alertFilterType]);

  const getAlertFilters = (sensorKit: SensorKit): SensorKitAlertFilters => {
    const baseFilters = {};

    switch (alertFilterType) {
      case "offline":
        return { ...baseFilters, sensor_kit_offline: sensorKit.serial_number };
      case "missing_data":
        return { ...baseFilters, sensor_kit_missing_data: sensorKit.serial_number };
      case "out_of_spec":
        return { ...baseFilters, sensor_kit_out_of_spec: sensorKit.serial_number };
      default:
        return baseFilters;
    }
  };

  const handleRowClick = (row: any) => {
    if (alertFilterType) {
      const sensorKit = row.original as SensorKit;
      setSelectedSensorKit(sensorKit);
      setIsAlertDialogOpen(true);
    }
  };

  return (
    <>
      <Card className="p-4">
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="w-full justify-between p-0 h-auto">
              <h3 className={`font-semibold ${titleColor}`}>{title} ({totalCount})</h3>
              {isOpen ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-4">
            <ThaloDataTable
              data={sensorKitsWithAlerts}
              columns={columns}
              isLoading={isLoading}
              pageCount={0}
              onRowClick={handleRowClick}
              showColumnFilter={false}
              infiniteScroll={true}
              hasNextPage={hasNextPage}
              fetchNextPage={fetchNextPage}
              isFetchingNextPage={isFetchingNextPage}
              initialRowCount={10}
              sorting={defaultSorting}
            />
          </CollapsibleContent>
        </Collapsible>
      </Card>

      <SensorKitDialog
        isOpen={isAlertDialogOpen}
        onOpenChange={setIsAlertDialogOpen}
        sensorKit={selectedSensorKit}
        alertFilters={selectedSensorKit ? getAlertFilters(selectedSensorKit) : null}
      />
    </>
  );
};
